import type {Theme} from '@emotion/react';

interface VariablesProps {
    theme: Theme;
}

/**
 * Generates CSS variables for the base state of number input components.
 *
 * @param props       The props object containing the theme.
 * @param props.theme The theme object with color definitions.
 * @returns CSS custom properties string for base input styling.
 */
export const getBaseVariables = ({theme}: VariablesProps) => `
    --text-input-background-color: ${theme.colors.neutral20};
    --text-input-border-color: ${theme.colors.neutral20};
    --text-input-font-color: ${theme.colors.textPrimary};
    --text-input-placeholder-font-color: ${theme.colors.textSecondary};
    --label-font-color: ${theme.colors.textPrimary};
`;

/**
 * Generates CSS variables for the disabled state of number input components.
 *
 * @param props       The props object containing the theme.
 * @param props.theme The theme object with color definitions.
 * @returns CSS custom properties string for disabled input styling.
 */
export const getDisabledVariables = ({theme}: VariablesProps) => `
    --text-input-font-color: ${theme.colors.neutral40};
    --text-input-placeholder-font-color: ${theme.colors.neutral40};
    --label-font-color: ${theme.colors.neutral60};
`;

/**
 * Generates CSS variables for the error state of number input components.
 *
 * @param props       The props object containing the theme.
 * @param props.theme The theme object with color definitions.
 * @returns CSS custom properties string for error input styling.
 */
export const getErrorVariables = ({theme}: VariablesProps) => `
    --text-input-border-color: ${theme.colors.error};
    --label-font-color: ${theme.colors.textPrimary};
`;

/**
 * Generates CSS variables for the focus state of number input components.
 *
 * @param props       The props object containing the theme.
 * @param props.theme The theme object with color definitions.
 * @returns CSS custom properties string for focused input styling.
 */
export const getFocusVariables = ({theme}: VariablesProps) => `
    --text-input-background-color: ${theme.colors.neutral0};
    --text-input-border-color: ${theme.colors.primary};
    --label-font-color: ${theme.colors.primary};
`;